import { apiService } from '@/feature/api/axios';

import type { AuditLogScope, IAuditLogEntry } from '../types/auditTrail';

const EVENT_TITLE_MAP: Record<string, string> = {
  AdminLogin: 'System Admin Login',
  OrganizationCreated: 'Organization Created',
  OrganizationUpdated: 'Organization Updated',
  OrganizationActivated: 'Organization Activated',
  OrganizationDeactivated: 'Organization Deactivated',
  OrganizationUserAdded: 'User Added to Org',
};

function toEventTitle(eventName: string): string {
  return EVENT_TITLE_MAP[eventName] ?? eventName.replace(/([A-Z])/g, ' $1').trim();
}

function toEventDescription(raw: Record<string, unknown>): string {
  const eventName = String(raw.event_name ?? '');
  const status = String(raw.status ?? '');
  const failureReason = raw.failure_reason ? String(raw.failure_reason) : null;

  if (failureReason && status === 'Failed') {
    return failureReason;
  }
  switch (eventName) {
    case 'OrganizationCreated': {
      const org = raw.organization as Record<string, unknown> | null;
      return org?.org_name
        ? `New organization created: ${org.org_name}`
        : 'New organization created';
    }
    case 'OrganizationDeactivated':
      return 'Organization status set to deactivated';
    case 'OrganizationActivated':
      return 'Organization status set to active';
    case 'OrganizationUpdated':
      return 'Organization details updated';
    case 'OrganizationUserAdded':
      return 'User was added to the organization';
    case 'AdminLogin':
      return status === 'Success' ? 'Successful admin login' : 'Admin login attempt';
    default:
      return '';
  }
}

function formatCategory(category: string): string {
  return category
    .split(/[\s_]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function toIso(value: unknown): string {
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isFinite(d.getTime()) ? d.toISOString() : new Date().toISOString();
  }
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value.toISOString() : new Date().toISOString();
  }
  return new Date().toISOString();
}

function mapRawEntry(
  scope: AuditLogScope,
  raw: Record<string, unknown>,
  idx: number
): IAuditLogEntry {
  const id = String(raw.id ?? `${scope}-${Date.now()}-${idx}`);

  const eventName = String(raw.event_name ?? '');
  const eventTitle = toEventTitle(eventName);
  const eventDescription = toEventDescription(raw);

  const category = formatCategory(String(raw.category ?? ''));

  const org = raw.organization as Record<string, unknown> | null;
  const organizationName = org?.org_name ? String(org.org_name) : '—';
  const organizationId = org?.id ? String(org.id) : undefined;

  const performedBy = raw.performed_by as Record<string, unknown> | null;
  const performedByName = performedBy?.email ? String(performedBy.email) : '—';
  const performedByRole = 'Super Admin';

  const timestamp = toIso(raw.performed_at);
  const status = String(raw.status ?? 'Unknown');

  return {
    id,
    scope,
    eventTitle,
    eventDescription: eventDescription || undefined,
    eventType: category,
    organizationId,
    organizationName,
    performedByName,
    performedByRole,
    category,
    timestamp,
    status,
    metadata: raw,
  };
}

export const auditTrailService = {
  getAuditLogs: async (args: { scope: AuditLogScope }): Promise<IAuditLogEntry[]> => {
    const api = apiService();
    const portal = args.scope === 'admin' ? 'admin' : 'app';

    try {
      const resp = await api.get<Record<string, unknown>>('/api/ai-automation-admin/audit-log/', {
        portal,
      });

      const raw = resp as Record<string, unknown>;
      const list = Array.isArray(raw.audit_logs) ? raw.audit_logs : [];

      return list
        .filter((x): x is Record<string, unknown> => x !== null && typeof x === 'object')
        .map((x, idx) => mapRawEntry(args.scope, x, idx));
    } catch {
      return [];
    }
  },
};
